import { CircularProgress } from "@mui/material";
import { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { Col, Row } from "reactstrap";
import ButtonComponent from "../../../components/ButtonComponent";
import { useMobile } from "../../../components/Layout/UseResponsive";
import { useAuth } from "../../../context/AuthContext";
import { useAvailableModulesContext } from "../../../context/AvailableModulesContext";
import Toast from "../../../helpers/Toast";
import { Utilities } from "../../../helpers/Utilities";
import { AssistanceListDTO } from "../../../models/Assistance/AssistanceListDTO";
import { GetAssistanceDTO } from "../../../models/Assistance/GetAssistanceDTO";
import { Parameter } from "../../../models/Parameter";
import { BigButton } from "../../../pages/workshop/BigButton";
import { AssistanceService } from "../../../services/AssistanceService";

export default function AssistanceWorkshopListB() {
    //Pagina
    const navigate = useNavigate();
    const isTwoCardsPerRow = useMobile(840);
    const isThreeCardsPerRow = useMobile(1085);
    const { technicianCode } = useAuth();
    const [isLoadingResults, setIsLoadingResults] = useState<boolean>(false);
    const assistanceService = new AssistanceService();
    //Resultados
    const [assistancesData, setAssistancesData] = useState<AssistanceListDTO[]>([]);
    const [page] = useState<number>(Utilities.LoadPageFromURLQuery("page"));
    const [pageCount, setPageCount] = useState(0);
    const [currentPage, setCurrentPage] = useState<number>();
    const [ShowAll, setShowAll] = useState<boolean>(Utilities.LoadParameterFromURLQuery("showAll", "string", false));
    const { availableModules, workshopCardsPerPage } = useAvailableModulesContext();
    const isClientNameAndModelView = availableModules.includes("ShowClientNameAndModelForAssistanceCardsWorkshopView");
    const pageSize = workshopCardsPerPage > 0 ? workshopCardsPerPage : 6;
    const workshopCardHeight = "300px";
    const workshopCardMinHeight = isClientNameAndModelView ? "170px" : undefined;
    const workshopCardPadding = isClientNameAndModelView ? "16px 0" : undefined;
    const workshopCardMinWidth = isTwoCardsPerRow ? "220px" : "250px";

    useEffect(() => {
        fetchData(page ?? 1);
    }, [ShowAll, page, pageSize]);

    useEffect(() => {
        const searchUrl: string = Utilities.CreateSearchURL([
            `page=${currentPage ?? 1}`,
            Utilities.AddParamToQueryIfValue("showAll", "string", ShowAll),
            Utilities.AddParamToQueryIfValue("technician", "string", technicianCode)
        ]);

    }, [ShowAll, technicianCode, currentPage]);

    const fetchData = async (page: number) => {
        setIsLoadingResults(true);

        var searchUrl: string = Utilities.CreateSearchURL([
            `page=${page}`,
            Utilities.AddParamToQueryIfValue("showAll", "string", ShowAll),
            Utilities.AddParamToQueryIfValue("technician", "string", technicianCode)
        ]);

        navigate(`/workshop/assistances${searchUrl}`, { replace: true });

        var response = await assistanceService.GetAll(mountSearchParameters(), Utilities.MountSortParameters([]), page, pageSize);

        if (response.success !== true) {
            setAssistancesData([]);
            setPageCount(0);
            setIsLoadingResults(false);
            Toast.Show("error", response.message);
            return;
        }

        setAssistancesData(response.items);
        setPageCount(response.totalPages);
        setIsLoadingResults(false);
        setCurrentPage(page);
    };

    const mountSearchParameters = (): Parameter[] => {
        var parameters: Parameter[] = [];
        parameters.push(new Parameter("status", "ABERTURA,ORÇAMENTO_ACEITE,EM_CURSO"));
        if (ShowAll === false) {
            parameters.push(new Parameter("technician", technicianCode === null ? "vazio" : technicianCode));
        }
        return parameters;
    };

    const getAssistance = async (docType: string, serie: string, number: number) => {
        setIsLoadingResults(true);
        var assistance: GetAssistanceDTO = {
            docType: docType,
            serie: serie,
            number: number,
        };

        var response = await assistanceService.GetAssistance(assistance);

        if (response.success === false) {
            Toast.Show("error", response.message);
            setIsLoadingResults(false);
            return;
        }

        navigate(`/Workshop/Assistance/${response.obj?.id}`);
        setIsLoadingResults(false);
    };

    const handleNavigation = async (assistanceCardPosition: number) => {
        if (assistanceCardPosition < 0) {
            assistanceCardPosition = 0;
        }
        if (assistanceCardPosition >= 0 && assistanceCardPosition < assistancesData.length) {
            navigate(`/Workshop/Assistance/${assistancesData[assistanceCardPosition].assistanceId}`);
        } else {
            console.error("Index (negativo):", assistanceCardPosition);
        }
    };

    const buildWorkshopButtonLabel = (assistance: AssistanceListDTO): string => {
        const documentInfo = `${assistance.docType} ${assistance.serie} ${assistance.number}`;

        if (isClientNameAndModelView) {
            return `${assistance.workshopInternalNumber ?? ""}\n\n${documentInfo}\n\n${assistance.client?.name || ""}\n\n${assistance.equipment?.model || ""}`;
        }

        return `${documentInfo}\n${assistance.equipment?.carPlate || ""}\n\n${assistance.status}\n\n${assistance.workshopInternalNumber ?? ""}`;
    };

    const renderAssistanceCard = (assistance: AssistanceListDTO, assistanceIndex: number) => (
        <Col
            key={assistanceIndex}
            xl={"auto"}
            lg={"auto"}
            md={"auto"}
            sm={6}
            xs={6}
            style={{ display: "flex", justifyContent: "center", alignItems: "center", minWidth: workshopCardMinWidth, marginTop: "11px", paddingLeft: "6px", paddingRight: "6px" }}
        >
            <BigButton
                path={""}
                onClickFunction={() =>
                    assistance.assistanceId ?
                        handleNavigation(assistanceIndex) :
                        getAssistance(
                            assistance.docType,
                            assistance.serie,
                            assistance.number
                        )
                }
                label={buildWorkshopButtonLabel(assistance)}
                height={workshopCardHeight}
                minHeight={workshopCardMinHeight}
                minWidth={workshopCardMinWidth}
                fontSize={"18px"}
                padding={workshopCardPadding}
                lineHeight={isClientNameAndModelView ? "1.5" : "1"}
            />
        </Col>
    );

    const intl = useIntl();

    return (
        <>
            <Row style={{ justifyContent: "center" }}>
                {isLoadingResults === false ? (
                    <Col xl={3} lg={4} md={5} sm={9} xs={12}>
                        <ButtonComponent
                            onClick={() => setShowAll(!ShowAll)}
                            text={intl.formatMessage({ id: ShowAll ? "AssistanceWorkshopListB.ShowMine" : "AssistanceWorkshopListB.ShowAll" })}
                            style={{ width: "95%" }}
                        />
                    </Col>
                ) : (
                    ""
                )}
            </Row>
            {isLoadingResults ? (
                <Row style={{ justifyContent: "center", alignItems: "center" }}>
                    <div
                        className="loading"
                        style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                        }}
                    >
                        <span>
                            {" "}
                            <FormattedMessage id="Common.Loading.DataLoading" />
                            ...{" "}
                        </span>
                        <CircularProgress style={{ marginLeft: "10px" }} size={"25px"} />
                    </div>
                </Row>
            ) : (
                assistancesData.length > 0 && (
                    <Row>
                        {Array.from({ length: Math.ceil(assistancesData.length / (isTwoCardsPerRow ? 2 : isThreeCardsPerRow ? 3 : 4)) }, (_, rowIndex) => {
                            const cardsPerRow = isTwoCardsPerRow ? 2 : isThreeCardsPerRow ? 3 : 4;
                            const startIndex = rowIndex * cardsPerRow;
                            const rowItems = assistancesData.slice(startIndex, startIndex + cardsPerRow);

                            return (
                                <Row key={rowIndex} style={{ justifyContent: "center", alignItems: "center", rowGap: "12px" }}>
                                    {rowItems.map((assistance, itemIndex) => {
                                        return renderAssistanceCard(assistance, startIndex + itemIndex);
                                    })}
                                </Row>
                            );
                        })}
                    </Row>
                )
            )}

            <Row style={{ justifyContent: "center", alignItems: "center", marginTop: "10px", marginRight: "16px" }}>
                <Col xl={1} lg={2} md={3} sm={3} xs={4} style={{ display: "flex", justifyContent: "right", alignItems: "center" }}>
                    <ButtonComponent
                        text={intl.formatMessage({ id: "AssistanceWorkshopListB.Previous" })}
                        onClick={() => fetchData(currentPage ? currentPage - 1 : 1)}
                        disabled={isLoadingResults || (currentPage && currentPage <= 1) ? true : false}
                    />
                </Col>
                <Col xl={2} lg={2} md={3} sm={3} xs={4} style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                    <span className="paginationNumberIndicator">
                        <FormattedMessage id="Common.Labels.Page" /> <strong>{currentPage}</strong>{" "}
                        <FormattedMessage id="Common.Labels.Of" /> <strong>{pageCount}</strong>
                    </span>
                </Col>
                <Col xl={1} lg={2} md={3} sm={3} xs={4} style={{ display: "flex", justifyContent: "left", alignItems: "center" }}>
                    <ButtonComponent
                        text={intl.formatMessage({ id: "AssistanceWorkshopListB.Next" })}
                        onClick={() => fetchData(currentPage ? currentPage + 1 : pageCount)}
                        disabled={isLoadingResults || (currentPage && currentPage >= pageCount) ? true : false}
                    />
                </Col>
            </Row>
        </>
    );
}
